import { useEffect, useRef, useState } from 'react';
import { FileAudioIcon, UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MAX_OPNAME_SECONDEN, duurInWoorden, duurKort, opnameTeLang } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { StapStippen } from '@/components/StapStippen';
import { ApiError } from '../../lib/apiClient';
import { Deelnemers, type RosterRegel } from './Deelnemers';
import { OpnamePaneel } from './OpnamePaneel';
import { useMaakGesprek } from './gesprekken.api';
import { Verwerkingsspoor } from '../review/Verwerkingsspoor';
import { leesDuur } from './duur';
import { uploadBestand } from './upload';

/** Nooit een datum in de toekomst: dat zou de standaardperiode van het overzicht
 *  voor iedereen naar een verzonnen jaar trekken (`standaardPeriode` in
 *  shared). De server weigert het ook, dit is alleen zodat het veld het zelf
 *  al niet aanbiedt. */
const vandaag = new Date().toISOString().slice(0, 10);

/**
 * Het opnamebestand kiezen of erop slepen.
 *
 * De kale `input type=file` gaf een knop met "Geen bestand gekozen" ernaast —
 * de vormgeving van de browser, die in niets op de rest van de applicatie lijkt
 * en ook niet laat zien dat je een bestand ook kunt slepen.
 */
function Kiezer({
  bestand,
  bezig,
  duur,
  onKies,
}: {
  bestand: File | null;
  bezig: boolean;
  /** `null` zolang hij nog wordt gelezen, of als de browser er niet bij kan. */
  duur: number | null;
  onKies: (b: File | null) => void;
}) {
  const veld = useRef<HTMLInputElement>(null);
  const [sleept, setSleept] = useState(false);

  if (bestand) {
    return (
      <Item variant="outline">
        <ItemMedia variant="icon">
          <FileAudioIcon />
        </ItemMedia>
        <ItemContent className="min-w-0">
          <ItemTitle className="truncate">{bestand.name}</ItemTitle>
          <ItemDescription>
            {(bestand.size / 1024 / 1024).toFixed(1)} MB
            {duur !== null && ` · ${duurKort(duur)}`}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button disabled={bezig} onClick={() => onKies(null)} size="sm" variant="ghost">
            Andere kiezen
          </Button>
        </ItemActions>
      </Item>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-6',
        sleept && 'border-primary bg-primary/5',
      )}
      onDragLeave={() => setSleept(false)}
      onDragOver={(e) => {
        e.preventDefault();
        if (!bezig) setSleept(true);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setSleept(false);
        if (bezig) return;
        const b = e.dataTransfer.files?.[0];
        if (b) onKies(b);
      }}
    >
      <input
        accept="audio/*,video/*"
        className="hidden"
        disabled={bezig}
        onChange={(e) => onKies(e.target.files?.[0] ?? null)}
        ref={veld}
        type="file"
      />
      <UploadIcon className="size-5 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Sleep de opname hierheen</span>
      <Button disabled={bezig} onClick={() => veld.current?.click()} size="sm" variant="outline">
        Bestand kiezen
      </Button>
    </div>
  );
}

/**
 * Stappen in dezelfde dialoog. Stond op één scherm, en dat werd twee dingen
 * tegelijk — een formulier invullen én daarna kijken hoe de upload vordert —
 * terwijl de deelnemerslijst (`Deelnemers`) inmiddels een eigen scherm waard is.
 *
 * Twee stappen, langs welke weg je ook binnenkomt: naam, datum en de opname
 * horen bij elkaar, daarna wie erbij waren. Of die opname uit een bestand komt
 * of uit de microfoon verandert alleen wat er op die eerste stap staat.
 *
 * Tijdens een lopende opname gaat het venster niet dicht: daar staat drie
 * kwartier op het spel.
 *
 * De volgorde verandert niets aan wat er gebeurt: aanmaken en uploaden blijft
 * één druk op de knop, op de laatste stap.
 */
export function NieuwGesprek({
  onSluit,
  begin,
  metOpname = false,
}: {
  onSluit: () => void;
  /** Een opname die er al is, met wat er onderweg is ingevuld. */
  begin?: { bestand?: File; titel?: string; datum?: string };
  /** Begin met opnemen in plaats van met een bestand kiezen. */
  metOpname?: boolean;
}) {
  const navigate = useNavigate();
  const maak = useMaakGesprek();

  const stappen = ['gegevens', 'deelnemers'] as const;
  const [stap, setStap] = useState(0);
  const huidig = stappen[stap];
  const [opnameLoopt, setOpnameLoopt] = useState(false);
  const [titel, setTitel] = useState(begin?.titel ?? '');
  const [datum, setDatum] = useState(begin?.datum || vandaag);
  const [roster, setRoster] = useState<RosterRegel[]>([]);
  const [bestand, setBestand] = useState<File | null>(begin?.bestand ?? null);
  const [duur, setDuur] = useState<number | null>(null);
  const [voortgang, setVoortgang] = useState<number | null>(null);
  const [fout, setFout] = useState('');
  /** Het gesprek bestaat al zodra de upload begint. Bij een mislukte poging
   *  hergebruiken we het, anders levert elke druk op de knop een nieuw leeg
   *  gesprek op — en die blijven staan tot de nachtelijke opruiming ze na een
   *  week weghaalt. */
  const [gesprekId, setGesprekId] = useState<string | null>(null);

  // De speelduur bepaalt of dit gesprek in één keer door de transcriptie past,
  // en dat weten we hier al — vóór een upload van tientallen minuten en vóór een
  // betaalde poging die tóch strandt. Geldt voor een gesleept bestand én voor
  // een opname uit de app zelf: allebei komen ze hier als `bestand` binnen.
  useEffect(() => {
    setDuur(null);
    if (!bestand) return;
    let actueel = true;
    void leesDuur(bestand).then((d) => {
      if (actueel) setDuur(d);
    });
    return () => {
      actueel = false;
    };
  }, [bestand]);

  const teLang = opnameTeLang(duur);

  async function opslaan() {
    setFout('');
    if (!bestand) return setFout('Kies een opname');
    if (roster.length === 0) return setFout('Geef aan wie erbij waren');

    try {
      const id =
        gesprekId ??
        (
          await maak.mutateAsync({
            occurredAt: new Date(datum).toISOString(),
            titel,
            roster: roster.map((r) => ({
              participantId: r.participantId,
              seniority: r.seniority,
            })),
          })
        ).id;
      setGesprekId(id);

      setVoortgang(0);
      // Het afronden van de upload start op de server ook het uitschrijven.
      await uploadBestand(id, bestand, setVoortgang);
      navigate(`/gesprekken/${id}/transcript`);
    } catch (err) {
      setVoortgang(null);
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  const bezig = voortgang !== null;
  const procent = Math.round((voortgang ?? 0) * 100);
  // Tijdens de upload en tijdens een lopende opname gaat het venster niet dicht:
  // in allebei de gevallen ben je iets kwijt dat niet terugkomt.
  const vast = bezig || opnameLoopt;

  return (
    <Dialog onOpenChange={(open) => !open && !vast && onSluit()} open>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" showCloseButton={!vast}>
        <DialogHeader>
          <DialogTitle>Nieuw gesprek</DialogTitle>
        </DialogHeader>

        {huidig === 'gegevens' ? (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="gesprek-titel">Naam van dit gesprek</FieldLabel>
              <Input
                autoFocus
                id="gesprek-titel"
                maxLength={120}
                onChange={(e) => setTitel(e.target.value)}
                placeholder="Bijvoorbeeld: Buddygesprek met Jamie"
                value={titel}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="gesprek-datum">Wanneer</FieldLabel>
              <Input
                id="gesprek-datum"
                max={vandaag}
                onChange={(e) => setDatum(e.target.value)}
                type="date"
                value={datum}
              />
            </Field>

            <Field>
              <FieldLabel>Opname</FieldLabel>
              {metOpname ? (
                <OpnamePaneel bestand={bestand} onBestand={setBestand} onLoopt={setOpnameLoopt} />
              ) : (
                <Kiezer bestand={bestand} bezig={false} duur={duur} onKies={setBestand} />
              )}
              {teLang && duur !== null && (
                <Alert variant="destructive">
                  <AlertTitle>
                    Deze opname duurt {duurInWoorden(duur)}. Er kan tot{' '}
                    {duurInWoorden(MAX_OPNAME_SECONDEN)} in één keer doorheen.
                  </AlertTitle>
                </Alert>
              )}
            </Field>
          </FieldGroup>
        ) : (
          <FieldGroup>
            <Deelnemers bezig={bezig} onChange={setRoster} />

            {fout && (
              <Alert variant="destructive">
                <AlertTitle>
                  {fout}
                  {gesprekId && (
                    <>
                      {' '}
                      Het gesprek is aangemaakt en de deelnemers zijn bewaard; opnieuw proberen
                      voegt geen tweede gesprek toe.
                    </>
                  )}
                </AlertTitle>
              </Alert>
            )}

            {bezig && (
              <Field>
                <Progress value={procent} />
                {/* Dezelfde balk als op de transcriptpagina, zodat de buddy één
                    proces ziet doorlopen in plaats van twee losse schermen. */}
                <Verwerkingsspoor actief="uploaden" bijschrift={`${procent}%`} />
                <span className="text-sm text-muted-foreground">
                  Laat dit scherm open staan tot het uploaden klaar is.
                </span>
              </Field>
            )}
          </FieldGroup>
        )}

        <StapStippen aantal={stappen.length} stap={stap} />

        {/* Geen Annuleren: het kruisje rechtsboven doet hetzelfde, en tijdens de
            upload of een lopende opname is dat kruisje er bewust niet. */}
        <DialogFooter>
          {stap > 0 && (
            <Button disabled={vast} onClick={() => setStap(stap - 1)} variant="ghost">
              Terug
            </Button>
          )}
          {huidig === 'deelnemers' ? (
            <Button disabled={bezig || roster.length === 0} onClick={opslaan}>
              {bezig && <Spinner data-icon="inline-start" />}
              {bezig ? 'Bezig…' : gesprekId ? 'Opnieuw proberen' : 'Aanmaken en uploaden'}
            </Button>
          ) : (
            <Button disabled={!bestand || opnameLoopt || teLang} onClick={() => setStap(stap + 1)}>
              Volgende
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
